import type { Rng } from '../rng.ts';
import type { MapDef } from '../maps/types.ts';
import type {
  ActionError,
  Clue,
  Difficulty,
  EventAudience,
  Language,
  LastError,
  PlayerId,
  PuzzleAct,
  PuzzleInstance,
  PuzzleType,
  Role,
  RoomId,
} from '../types.ts';

export interface PuzzlePlayer {
  id: PlayerId;
  role: Role;
  seat: number;
  expelled: boolean;
  coins: number;
  petards: number;
}

export interface GenContext {
  rng: Rng;
  /** 1–5. */
  level: number;
  /** Roles in the game (3, 4 or 5 of them). */
  roles: readonly Role[];
  players: readonly PuzzlePlayer[];
  language: Language;
  room: RoomId;
  map: MapDef;
  difficulty: Difficulty;
  /** Alarm in tenths when the puzzle is created (matters for the escape). */
  alarmTenths: number;
  /** Corridors only: guards removed by the map (GDD §10.3) and vision limits. */
  guardDelta: number;
  dimVision: boolean;
}

export interface ActContext {
  actor: PlayerId;
  actorRole: Role;
  players: readonly PuzzlePlayer[];
  execMs: number;
  alarmTenths: number;
  /** Cameras looped by the hacker (GDD §3.2). */
  looped: boolean;
  /** In a tunnel on the night train. */
  tunnel: boolean;
  dimVision: boolean;
}

export interface ViewContext {
  playerId: PlayerId;
  role: Role;
  roles: readonly Role[];
  players: readonly PuzzlePlayer[];
  execMs: number;
  alarmTenths: number;
  tunnel: boolean;
  /** `true` once the reveal started: secrets may be shown. */
  revealed: boolean;
}

export interface TickContext {
  execMs: number;
  alarmTenths: number;
  players: readonly PuzzlePlayer[];
  roles: readonly Role[];
  tunnel: boolean;
  dimVision: boolean;
}

/** Something the puzzle wants announced. Audience defaults to everyone. */
export interface PuzzleEvent {
  type: string;
  audience?: EventAudience;
  data: Record<string, unknown>;
}

export interface ApplyOk {
  ok: true;
  solved: boolean;
  /** Error penalty in alarm tenths *before* the difficulty multiplier. */
  errorTenths: number;
  /** Fixed alarm (not scaled), e.g. the firecracker. */
  fixedTenths: number;
  /** Information that lets Plan B give the attempt back. */
  undo: LastError['undo'];
  /** Power consumed by this action. */
  consumes: 'coin' | 'petard' | null;
  /** The corridor reported a sighting (the disguise may absorb it). */
  sighted: boolean;
  events: PuzzleEvent[];
}

export interface ApplyFail {
  ok: false;
  error: ActionError;
}

export type ApplyResult = ApplyOk | ApplyFail;

export interface TickOk {
  events: PuzzleEvent[];
  errorTenths: number;
  sighted: boolean;
  solved: boolean;
}

export interface SolveInfo {
  solvable: boolean;
  /** A number that grows with difficulty (moves, attempts, search size…). */
  difficulty: number;
}

export interface TamperChoice {
  clueId: string;
  value: unknown;
}

export interface PuzzleModule {
  type: PuzzleType;
  generate(ctx: GenContext): {
    params: unknown;
    state: unknown;
    clues: Clue[];
    solvers: Role[];
  };
  apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext): ApplyResult;
  /** Called for every simulation step while the puzzle is active. */
  tick?(p: PuzzleInstance, dtMs: number, ctx: TickContext): TickOk;
  /** What `role` is allowed to see (allow-list: build, never strip). */
  view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown>;
  /** All concrete interactions available to `role` right now. */
  legalActs(p: PuzzleInstance, role: Role, ctx: ActContext): PuzzleAct[];
  /** Picks an alterable clue and the wrong value to show; null when nothing can be altered. */
  tamper(p: PuzzleInstance, rng: Rng, excludeHolder: Role | null): TamperChoice | null;
  /** Gear and Jammer/EMP: solves it outright. */
  autoSolve?(p: PuzzleInstance): void;
  /** Plan B: gives back the last attempt. */
  undo?(p: PuzzleInstance, undo: NonNullable<LastError['undo']>): void;
  /** The solver proves there is at least one solution and measures difficulty. */
  solve(params: unknown): SolveInfo;
}

/** The value a clue holder actually sees: the tampered one when present. */
export function shown(c: Clue): unknown {
  return c.tampered ? c.tampered.value : c.value;
}

/** First role of the preference list that is in the game. */
export function pickHolder(roles: readonly Role[], prefs: readonly Role[]): Role {
  const found = prefs.find((r) => roles.includes(r));
  if (!found) throw new Error(`no holder available among ${prefs.join(',')}`);
  return found;
}

export const okResult = (over: Partial<ApplyOk> = {}): ApplyOk => ({
  ok: true,
  solved: false,
  errorTenths: 0,
  fixedTenths: 0,
  undo: null,
  consumes: null,
  sighted: false,
  events: [],
  ...over,
});

export const failResult = (code: ActionError['code'], message: string): ApplyFail => ({
  ok: false,
  error: { code, message },
});
